/* eslint-disable @nx/enforce-module-boundaries */
import { CloseIcon, ThreeDot } from '@mybridge/icons';
import { ShareIcon } from '@mybridge/icons/Share';
import { CloseFileIcon } from '@mybridge/icons/Close';
import {
  Avatar,
  HStack,
  IconButton,
  Link,
  Spinner,
  Stack,
  Text,
  Tooltip,
  Box,
  Grid,
  GridItem,
} from '@mybridge/ui';
import { useDimensions } from '@mybridge/ui/hooks';
import { SelectableTable } from '@mybridge/ui/table';
import moment from 'moment';
import { forwardRef, use, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  copyTextToClipboard,
  isRootDir,
  isSharedUser,
  isTeamDir,
  toPascalCase,
  humanFileSize,
  isDir,
  isFile,
} from 'v4/components/utils';
import { getProfilePic, getUserFullName } from 'v4/lib/commons';
import { DiskContext } from '../context';
import { FileFolderIcon } from '../my-disk/file-icon';
import { MyDiskItemMenu } from '../my-disk/listing-item-menu';
import { DiskNoFilesIllustration } from '../no-files-illustration';
import { RootDirIcon } from '../my-disk/main-menu/root-dir-icon';
import { isMobile } from 'react-device-detect';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { useBranchLink } from 'v4/components/branch-sdk/branch-create';
import { TeamsContext } from 'v4/snippets/teams/context';

export const DiskListingTable = forwardRef(
  ({ menu, accessType = 'W', accessTypeTeamAccess, ...props }, ref) => {
    const {
      files,
      setSelectedDir,
      selectedFile,
      setSelectedFile,
      filesLoading,
      checkedFiles,
      setSelectedFiles,
      generateShareLink,
      setSelectedMainDir,
      sharedFileLink,
      refetchSharedFileLink,
      shareDisc,
      toast,
      userProfileInfo,
      showPreview,
      setShowPreview,
    } = useContext(DiskContext);
    const {
      teamDetails,
    } = useContext(TeamsContext);
    const [hovered, setHovered] = useState();
    const [copyingLink, setCopyingLink] = useState(false);
    const { t } = useTranslator();
    const direction = useDirection();
    const { createBranchLink } = useBranchLink();
    const userSlug = userProfileInfo?.slug || 'anonymous';

    useEffect(()=>{
      trackEvent(ANALYTICS_EVENTS.MyDiskDetail,userSlug)
    },[userSlug])

    const cols = useMemo(
      () => [
        {
          key: '__icon',
          title: <Text textAlign="center"> </Text>, // Empty title for the first icon column
          dataKey: '',
          // width: 50,
          frozen: 'left',
          showOnMobile: true,
          cellRenderer: ({ cellData, rowData }) => {
            return isRootDir(rowData) ? (
              <RootDirIcon dir={rowData} width="20" height="20" />
            ) : isTeamDir(rowData) ? (
              <Avatar
                size="sm"
                src={getProfilePic(rowData?.team_info)}
                name={rowData?.team_info?.name}
              />
            ) : isSharedUser(rowData) ? (
              <Avatar
                size="sm"
                src={getProfilePic(rowData || rowData?.file?.file)}
                name={getUserFullName(rowData || rowData?.file)}
              />
            ) : (
              <FileFolderIcon
                file={rowData || rowData?.file?.file}
                width="20"
                height="20"
              />
            );
          },
        },
        {
          key: 'name',
          title: (
            <Text
              // marginLeft="16px"
              fontSize="medium"
              fontWeight="600"
              textAlign="left"
              width="100%"
            >
              {t('myDisk.name') || "Name"}
            </Text>
          ), // Left-aligned header
          dataKey: 'name',
          width: isMobile ? '70%' : '25%',
          resizable: true,
          sortable: false,
          showOnMobile: true,
          cellRenderer: ({ cellData, rowData }) => {
            // Logging rowData and cellData here

            return (
              <Tooltip label={getUserFullName(rowData) || rowData?.file?.name}  hasArrow placement="top">
               <Box maxWidth="70%">
                <Text
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  fontSize="smaller"
                  textAlign="left"
                  width="100%"
                >
                  <Link
                    textTransform={isFolder(rowData)?"capitalize":""}
                    {...(isFolder(rowData)
                      ? {
                          onClick: (e) => {
                            if (isRootDir(rowData)) {
                              setSelectedMainDir(rowData);
                            }
                            setSelectedDir(rowData);
                          },
                        }
                      : {
                          onClick: (e) => {
                            setShowPreview(true);
                          },
                        })}
                  >
                    {isRootDir(rowData) || rowData?.type === 'RD'
                      ? t(`myDisk.${cellData}`) || toPascalCase(cellData)
                      : isTeamDir(rowData)
                      ? rowData?.team_info?.name
                      : cellData ||
                        getUserFullName(rowData) ||
                        rowData?.file?.name}
                  </Link>
                </Text>
               </Box>
              </Tooltip>
            );
          },
        },
        {
          key: 'shared_with',
          title: (
            <Text
              // marginLeft="16px"
              fontSize="medium"
              fontWeight="600"
              textAlign="center"
              width="100%"
            >
              {t('myDisk.whoCanAccess') || "Who can access"}
            </Text>
          ), // Center-aligned header
          dataKey: 'shared_with',
          width: '40%',

          resizable: true,
          sortable: false,
          showOnMobile: false,
          cellRenderer: ({ cellData, rowData }) => {

            return (
              <Box maxWidth="70%">
                <Text
                 overflow="hidden"
                 whiteSpace="nowrap"
                 textOverflow="ellipsis"
                 fontSize="smaller"
                 textAlign="left"
                 width="100%"
                >
                  {cellData?.length === 0 ||
                  rowData?.file?.shared_with?.length === 0
                    ? '-'
                    : `${
                        cellData?.length || rowData?.file?.shared_with?.length
                      } ${
                        cellData?.length ||
                        rowData?.file?.shared_with?.length > 1
                          ? t('myMessages.members') || 'members'
                          : t('otherModules.member') || 'member'
                      }`}
                </Text>
              </Box>
            );
          },
        },
        {
          key: 'updated_at',
          title: (
            <Text
              marginLeft="1px"
              fontSize="medium"
              fontWeight="600"
              textAlign="left"
              width="100%"
            >
              {t('myDisk.modified') || "Modified"}
            </Text>
          ), // Center-aligned header
          dataKey: 'updated_at',
          resizable: true,
          sortable: false,
          frozen: 'right',
          width: '30%',
          cellRenderer: ({ cellData, rowData, column }) => {
            // Use the updated_at from rowData?.file if available, else use cellData
            const updatedAt = cellData || rowData?.file?.updated_at;

            return (
              <Text
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              fontSize="smaller"
              // textAlign="left"
              {...(direction === 'rtl') ? {textAlign: 'right'} : {textAlign: 'left'}}
              width="100%"
              >
                {hovered?.id === rowData?.id &&
                rowData?.type !== 'RD' &&
                rowData?.owner?.id === userProfileInfo?.id &&
                (rowData?.team_ref === null || rowData?.accessType === 2) ? (
                  <HStack justifyContent="center">
                    {copyingLink ? (
                      <Spinner size="sm" />
                    ) : (
                       rowData?.type ===  "F" && <Text onClick={onCopyLinkClick(rowData)} as={Link}>
                        {t('post.copyLink') || "Copy Link"}
                      </Text>
                    )}
                    <Tooltip label="Share">
                      <IconButton
                        onClick={(e) => {
                          setSelectedFile?.(rowData);
                          shareDisc?.onOpen?.();
                        }}
                        size="sm"
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  </HStack>
                ) : (
                  moment(updatedAt)?.format('MM/DD/YYYY hh:mm a')
                )}
              </Text>
            );
          },
        },
        {
          key: '__menu',
          title: '', // Empty title for the last column
          dataKey: '',
          frozen: 'right',
          showOnMobile: true,
          cellRenderer: ({ cellData, rowData }) => {
            // console.log("rwdata", accessType);
            return rowData?.type !== 'RD' ? (
              menu ?? (
                <MyDiskItemMenu
                  file={rowData}
                  isOwner={(rowData?.owner?.id === userProfileInfo?.id) || (userProfileInfo?.id === teamDetails?.disk_payer_details?.id) || (rowData?.created_by === userProfileInfo?.id && rowData?.shared_with?.length !== 0)}
                  accessType={accessType}
                  // permissionsType={accessTypeTeamAccess === 'W' ? 2 : 1 ?? rowData?.access_type}
                  permissionsType={rowData?.access_type}
                  teamDetails={rowData?.team_ref ? rowData?.team_info : null}
                >
                  <IconButton variant="ghost">
                    <ThreeDot width="20" height="20" />
                  </IconButton>
                </MyDiskItemMenu>
              )
            ) : (
              <></>
            );
          },
        },
      ],
      [files, checkedFiles, hovered, copyingLink]
    );

    // console.log('selectedFile', selectedFile?.owner?.id === userProfileInfo?.id)
    const tableParentRef = useRef();
    const tabelParentSize = useDimensions(tableParentRef, true)?.borderBox;
    const { width = 640, height = 480 } = tabelParentSize ?? {};

    const handleGenerateLink = async () => {
      try {
        setCopyingLink(true);
        const url = await createBranchLink({
          itemType: 'disk',
          itemId: selectedFile?.id,
          itemData: {
            "accessType": 1,
            "name": selectedFile?.name,
            "type": selectedFile?.type || "F",
            "file": selectedFile?.file
          },
        });
        if (url) {
          copyLink(url);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setCopyingLink(false);
      }
    };

    const copyLink = async (url) => {
      try {
        await copyTextToClipboard(url || sharedFileLink?.public_share_deep_link);
        toast({ title: t('myDisk.linkCopiedSuccessfully') || 'Link copied successfully!', status: 'success',position: 'top' });
      } catch (e) {
        toast({ title: t('toast.failedToCopyLink') || 'Failed to copy link', status: 'error' });
      }
    };

    const onCopyLinkClick = (file) => async (e) => {
      // try {
      //   setCopyingLink(true);
      //   setSelectedFile?.(file);
      //   await refetchSharedFileLink?.();

      //   if (!sharedFileLink || sharedFileLink?.status_code > 400) {
      //     if (
      //       !sharedFileLink ||
      //       sharedFileLink?.detail?.includes?.('not have public share link')
      //     ) {
      //       try {
      //         await generateShareLink?.(file);
      //       } catch (e) {
      //         console.log(e);
      //       }
      //     }
      //   } else {
      //     copyTextToClipboard(sharedFileLink?.public_share_deep_link);
      //     toast({
      //       title: t('myDisk.linkCopiedSuccessfully') || 'Link copied successfully!',
      //       status: 'success',
      //       position: 'top',
      //     });
      //   }
      // } catch (e) {
      //   toast({ title: e.message, status: 'error' });
      // } finally {
      //    
      // }
      handleGenerateLink();
    };

    const onFileSelect = (file) => {
      setSelectedFile(file);
      // setShowPreview(true)
    };

    const isFolder = (file) => file?.type === 'D' || file?.type === 'RD';

    return (
      <>
        <HStack
          align="flext-start"
          position="relative"
          width="100%"
          height="100%"
        >
          <Stack ref={tableParentRef} width={'100%'} overflowX={'hidden'}>
            {/* table view */}
            <SelectableTable
              onSelectedRowsChange={(rows) => setSelectedFiles(rows)}
              emptyRenderer={() => (
                <DiskNoFilesIllustration loading={filesLoading} />
              )}
              rowKey="rowKey"
              selectable={true}
              width={width}
              height={height}
              columns={
                isMobile ? cols.filter((item) => item.showOnMobile) : cols
              }
              data={files?.map?.((f, i) => ({ ...f, rowKey: f?.id + '_' + i }))}
              rowClassName={({ rowData }) =>
                rowData?.id === selectedFile?.id ? `active` : ``
              }
              hovered={hovered}
              rowEventHandlers={{
                onMouseEnter: ({ rowData }) => {
                  setHovered(rowData);
                },
                onMouseLeave: ({ rowData }) => {
                  setHovered(null);
                },
                onClick: ({ rowData }) => onFileSelect(rowData),
                onDoubleClick: ({ rowData }) => {
                  if (rowData?.type === 'F') {
                    setSelectedFile(rowData);
                    setShowPreview(true);
                  } else {
                    setSelectedDir(rowData);
                  }
                },
              }}
            />
          </Stack>
        </HStack>
      </>
    );
  }
);
